import paddleAPI from '~/apis/paddleApi'

// Shared Paddle.js loader/initializer + live price preview. Prices are sourced from Paddle
// (single source of truth) rather than hardcoded in the app.

interface PaddlePricePreview {
  formattedTotal: string
  formattedPerSeat: string
  currencyCode: string
}

interface PaddleTier {
  minUsers: number
  maxUsers: number
  monthlyPriceId: string
  annualPriceId: string
  name: string
}

let paddlePromise: Promise<any> | null = null
let tiersPromise: Promise<PaddleTier[]> | null = null

// Paddle.js v2 allows a single Paddle.Initialize (and thus a single eventCallback) per page.
// We register one dispatcher here and fan events out to any subscribers (e.g. inline checkouts),
// so callers must NOT call Paddle.Initialize themselves — subscribe via onPaddleEvent instead.
type PaddleEventHandler = (event: any) => void
const paddleEventHandlers = new Set<PaddleEventHandler>()

const loadPaddleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Paddle.js can only load in the browser'))
      return
    }
    if ((window as any).Paddle) {
      resolve()
      return
    }
    const existing = document.querySelector('script[src*="paddle.js"]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Paddle.js')),
      )
      return
    }
    const script = document.createElement('script')
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Paddle.js'))
    document.head.appendChild(script)
  })
}

export const usePaddle = () => {
  // Loads Paddle.js and initializes it with the backend-provided env + client token. Cached.
  const ensurePaddle = (): Promise<any> => {
    if (paddlePromise) return paddlePromise
    paddlePromise = (async () => {
      await loadPaddleScript()
      const Paddle = (window as any).Paddle
      const config = await paddleAPI.getPaddleConfig()
      if (!config.clientToken) {
        throw new Error('Paddle client token is not configured')
      }
      if (config.env === 'sandbox') {
        Paddle.Environment.set('sandbox')
      }
      Paddle.Initialize({
        token: config.clientToken,
        eventCallback: (event: any) => {
          for (const handler of paddleEventHandlers) {
            try {
              handler(event)
            } catch (err) {
              console.error('Paddle event handler failed:', err)
            }
          }
        },
      })
      return Paddle
    })().catch((err) => {
      paddlePromise = null // allow retry on failure
      throw err
    })
    return paddlePromise
  }

  // Fetches and caches the pricing tiers (price IDs) from the backend.
  const getTiers = (): Promise<PaddleTier[]> => {
    if (tiersPromise) return tiersPromise
    tiersPromise = paddleAPI
      .getPricingTiers()
      .then((res) => (res?.tiers ?? []) as PaddleTier[])
      .catch((err) => {
        tiersPromise = null
        throw err
      })
    return tiersPromise
  }

  const priceIdForSeats = (
    tiers: PaddleTier[],
    seats: number,
    isAnnual: boolean,
  ): string | null => {
    const tier = tiers.find((t) => seats >= t.minUsers && seats <= t.maxUsers)
    if (!tier) return null
    return isAnnual ? tier.annualPriceId : tier.monthlyPriceId
  }

  // Returns localized, Paddle-sourced totals for a given seat count + billing cycle.
  const previewForSeats = async (
    seats: number,
    isAnnual: boolean,
  ): Promise<PaddlePricePreview | null> => {
    const tiers = await getTiers()
    const priceId = priceIdForSeats(tiers, seats, isAnnual)
    if (!priceId) return null

    const Paddle = await ensurePaddle()
    const result = await Paddle.PricePreview({
      items: [{ priceId, quantity: seats }],
    })
    const lineItem = result?.data?.details?.lineItems?.[0]
    if (!lineItem) return null

    return {
      formattedTotal: lineItem.formattedTotals?.total ?? '',
      formattedPerSeat: lineItem.formattedUnitTotals?.total ?? '',
      currencyCode: result.data?.currencyCode ?? 'USD',
    }
  }

  // Subscribe to Paddle checkout events (checkout.loaded/completed/closed/error, etc.).
  // Returns an unsubscribe function. Requires ensurePaddle() to have run (or run soon) so the
  // single global eventCallback is registered.
  const onPaddleEvent = (handler: PaddleEventHandler): (() => void) => {
    paddleEventHandlers.add(handler)
    return () => {
      paddleEventHandlers.delete(handler)
    }
  }

  return {
    ensurePaddle,
    getTiers,
    priceIdForSeats,
    previewForSeats,
    onPaddleEvent,
  }
}

export type { PaddlePricePreview, PaddleTier }
